import { createTheme, alpha } from '@mui/material/styles';

const defaultTheme = createTheme();


export const brand = {
  // Beige brand scale (based on #E5D3C0), reduced "brown" feel
  50: '#FDF9F4',
  100: '#F5EEE6',
  200: '#EDE1D4',
  300: '#E5D3C0', // brand tint (provided)
  400: '#D8C6B3',
  500: '#C6B39F',
  600: '#B19C86',
  700: '#A08F7C', // primary (taupe-beige)
  800: '#7D6E5E',
  900: '#5C5044',
};

export const gray = {
  // Warm neutrals (avoid cold blues)
  50: '#FFFFFF',
  100: '#FAF7F3',
  200: '#EFE7DE',
  300: '#E6DDD3',
  400: '#B8AEA4',
  500: '#8A7F76',
  600: '#5B524B',
  700: '#3A332E',
  800: '#1E1A16',
  900: '#0F0C0A',
};

export const green = {
  50: 'hsl(120, 80%, 98%)',
  100: 'hsl(120, 75%, 94%)',
  200: 'hsl(120, 75%, 87%)',
  300: 'hsl(120, 61%, 77%)',
  400: 'hsl(120, 44%, 53%)',
  500: 'hsl(120, 59%, 30%)',
  600: 'hsl(120, 70%, 25%)',
  700: 'hsl(120, 75%, 16%)',
  800: 'hsl(120, 84%, 10%)',
  900: 'hsl(120, 87%, 6%)',
};

export const orange = {
  50: 'hsl(45, 100%, 97%)',
  100: 'hsl(45, 92%, 90%)',
  200: 'hsl(45, 94%, 80%)',
  300: 'hsl(45, 90%, 65%)',
  400: 'hsl(45, 90%, 40%)',
  500: 'hsl(45, 90%, 35%)',
  600: 'hsl(45, 91%, 25%)',
  700: 'hsl(45, 94%, 20%)',
  800: 'hsl(45, 95%, 16%)',
  900: 'hsl(45, 93%, 12%)',
};

export const red = {
  50: 'hsl(0, 100%, 97%)',
  100: 'hsl(0, 92%, 90%)',
  200: 'hsl(0, 94%, 80%)',
  300: 'hsl(0, 90%, 65%)',
  400: 'hsl(0, 90%, 40%)',
  500: 'hsl(0, 90%, 30%)',
  600: 'hsl(0, 91%, 25%)',
  700: 'hsl(0, 94%, 18%)',
  800: 'hsl(0, 95%, 12%)',
  900: 'hsl(0, 93%, 6%)',
};

export const getDesignTokens = (mode) => {
 

  return {
    palette: {
      mode,
      primary: {
        light: brand[200],
        main: brand[700],
        dark: brand[800],
        contrastText: '#FFFFFF',
        ...(mode === 'dark' && {
          contrastText: '#FFFFFF',
          light: brand[600],
          main: brand[500],
          dark: brand[400],
        }),
      },
      info: {
        // Used in AppBar in the app; keep it light and airy
        light: brand[100],
        main: brand[100],
        dark: brand[200],
        contrastText: gray[800],
        ...(mode === 'dark' && {
          contrastText: '#F7F2EC',
          light: '#1A1512',
          main: '#1A1512',
          dark: '#14110E',
        }),
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
        ...(mode === 'dark' && {
          light: orange[400],
          main: orange[500],
          dark: orange[700],
        }),
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
        ...(mode === 'dark' && {
          light: red[400],
          main: red[500],
          dark: red[700],
        }),
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
        ...(mode === 'dark' && {
          light: green[400],
          main: green[500],
          dark: green[700],
        }),
      },
      grey: {
        ...gray,
      },
      divider: mode === 'dark' ? alpha(gray[700], 0.6) : alpha(gray[300], 0.7),
      background: {
        default: brand[50],
        paper: brand[100],
        ...(mode === 'dark' && { default: '#14110E', paper: '#1A1512' }),
      },
      text: {
        primary: gray[800],
        secondary: gray[600],
        warning: orange[400],
        ...(mode === 'dark' && {
          primary: '#F7F2EC',
          secondary: '#CFC5BB',
        }),
      },
      action: {
        hover: alpha(brand[300], 0.18),
        selected: `${alpha(brand[300], 0.26)}`,
        ...(mode === 'dark' && {
          hover: alpha(brand[300], 0.14),
          selected: alpha(brand[300], 0.20),
        }),
      },
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      wordWrap: "break-word",
      whiteSpace: 'normal',
      h1: {
        fontSize: defaultTheme.typography.pxToRem(48),
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: -0.5,
      },
      h2: {
        fontSize: defaultTheme.typography.pxToRem(36),
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h3: {
        fontSize: defaultTheme.typography.pxToRem(30),
        lineHeight: 1.2,
      },
      h4: {
        fontSize: defaultTheme.typography.pxToRem(24),
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h5: {
        fontSize: defaultTheme.typography.pxToRem(20),
        fontWeight: 600,
      },
      h6: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 600,
      },
      subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(18),
      },
      subtitle2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 500,
      },
      body1: {
        fontSize: defaultTheme.typography.pxToRem(14),
      },
      body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 400,
      },
      caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 400,
      },
    },
    shape: {
      borderRadius: 14,
    },
  };
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        light: brand[200],
        main: brand[700],
        dark: brand[800],
        contrastText: '#FFFFFF',
      },
      info: {
        light: brand[100],
        main: brand[100],
        dark: brand[200],
        contrastText: gray[800],
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[300], 0.7),
      background: {
        default: brand[50],
        paper: brand[100],
      },
      text: {
        primary: gray[800],
        secondary: gray[600],
        warning: orange[400],
        white: '#fff',
      },
      action: {
        hover: alpha(brand[300], 0.18),
        selected: `${alpha(brand[300], 0.26)}`,
      },
      baseShadow:
        'rgba(30, 26, 22, 0.06) 0px 1px 2px 0px, rgba(30, 26, 22, 0.08) 0px 12px 28px -4px',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: '#FFFFFF',
        light: brand[600],
        main: brand[500],
        dark: brand[400],
      },
      info: {
        contrastText: '#F7F2EC',
        light: '#1A1512',
        main: '#1A1512',
        dark: '#14110E',
      },
      warning: {
        light: orange[400],
        main: orange[500],
        dark: orange[700],
      },
      error: {
        light: red[400],
        main: red[500],
        dark: red[700],
      },
      success: {
        light: green[400],
        main: green[500],
        dark: green[700],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[700], 0.6),
      background: {
        default: '#14110E',
        paper: '#1A1512',
      },
      text: {
        primary: '#F7F2EC',
        secondary: '#CFC5BB',
        white: '#fff',
      },
      action: {
        hover: alpha(brand[300], 0.14),
        selected: alpha(brand[300], 0.20),
      },
      baseShadow:
        'rgba(0, 0, 0, 0.55) 0px 8px 26px 0px, rgba(0, 0, 0, 0.45) 0px 18px 46px -10px',
    },
  },
};

export const typography = {
  fontFamily: 'Inter, sans-serif',
  h1: {
    fontSize: defaultTheme.typography.pxToRem(48),
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(36),
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(30),
    lineHeight: 1.2,
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(18),
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14),
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};